import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, Loader2, Trash2, MessageSquare } from 'lucide-react';
import { Button, Pill, Slideout, EmptyState } from '../../index';
import { ccAnalyzeControl } from '../../../../services/api';

/**
 * AIAssistantSlideout — V2 Compliance AI Assistant (Phase B7 / G320).
 *
 * Replaces ?legacy=1&tab=AI_ASSISTANT. Production chat interface
 * that wraps the compliance-assist endpoint. Operators can ask:
 *
 *   "How does SOC 2 CC6.1 apply to our agentlet runtime?"
 *   "Which controls cover privileged-action audit logging?"
 *   "Generate a remediation plan for finding F-2401"
 *
 * The endpoint is `/api/v1/compliance-center/assist/analyze` —
 * accepts free-text questions with optional control / agent context.
 *
 * Conversation state lives in component memory only — no
 * persistence yet (backend conversation API not shipped).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
export function AIAssistantSlideout({ isOpen, onClose, addToast }) {
  const [messages, setMessages] = useState([]);   // { role: 'user' | 'assistant', text, at }
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const feedRef = useRef(null);

  /* Auto-scroll on new message. */
  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [messages.length]);

  const handleSend = async () => {
    const q = input.trim();
    if (!q || sending) return;
    const userMsg = { role: 'user', text: q, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    try {
      const r = await ccAnalyzeControl({ question: q, history: messages.slice(-6) });
      const responseText = r?.data?.answer || r?.data?.response || r?.answer
                        || r?.response || r?.text || 'No response from compliance assistant.';
      setMessages((m) => [...m, { role: 'assistant', text: responseText, at: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: `Assistant error: ${e?.message || e}. Try again or rephrase the question.`,
        at: new Date().toISOString(),
        error: true,
      }]);
    } finally { setSending(false); }
  };

  const handleClear = () => {
    setMessages([]);
    addToast?.('Conversation cleared.', 'info');
  };

  const SUGGESTED_PROMPTS = [
    'How does SOC 2 CC6.1 apply to our agentlet runtime?',
    'Which controls cover privileged-action audit logging?',
    'What evidence do I need for ISO 27001 A.9.2.3?',
    'Suggest a remediation plan for our 5 highest-risk findings.',
  ];

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={620}
      ariaLabel="Compliance AI Assistant"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Compliance AI Assistant</h2>
            <Pill tone="cyan" size="sm">Beta</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0">
            Ask about controls, mappings, remediation strategies. Responses are advisory —
            verify before applying to production policies.
          </p>
          {messages.length > 0 && (
            <div className="mt-2">
              <Button variant="ghost" size="sm" iconLeft={<Trash2 size={12} />}
                      onClick={handleClear}>
                Clear conversation ({messages.length})
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="flex flex-col" style={{ height: 'calc(100vh - 280px)', minHeight: 380 }}>
        {/* Feed */}
        <div ref={feedRef} className="flex-1 overflow-y-auto pr-1 mb-3 space-y-3">
          {messages.length === 0 && (
            <div className="py-8">
              <EmptyState
                icon={<MessageSquare size={20} />}
                title="Start a conversation"
                description="Ask anything about your compliance posture, control mappings, or remediation strategies."
              />
              <div className="mt-4 max-w-md mx-auto">
                <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-2">
                  Try one of these:
                </div>
                <div className="space-y-1.5">
                  {SUGGESTED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => setInput(p)}
                      className="w-full text-left text-[12px] px-3 py-2 bg-v2-surface border border-v2-border-subtle rounded-v2-sm hover:bg-v2-action-soft hover:border-v2-action/40 transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.text} at={m.at} error={m.error} />
          ))}
          {sending && (
            <Bubble role="assistant" pending text="Thinking…" />
          )}
        </div>

        {/* Input bar */}
        <div className="border-t border-v2-border-subtle pt-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
              }}
              placeholder="Ask a compliance question… (Shift+Enter for newline)"
              rows={2}
              disabled={sending}
              className="flex-1 border border-v2-border-subtle rounded-v2-sm px-3 py-2 text-[13px] bg-v2-surface focus:border-v2-action focus:ring-2 focus:ring-v2-action/20 focus:outline-none resize-y disabled:bg-v2-muted"
            />
            <Button variant="primary" iconLeft={sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    disabled={sending || !input.trim()}
                    onClick={handleSend}>
              {sending ? 'Sending' : 'Send'}
            </Button>
          </div>
          <p className="text-[10.5px] text-v2-ink-muted mt-1.5 m-0">
            AI Assistant uses anonymised tenant context. Don't paste secrets, credentials, or
            customer PII into questions.
          </p>
        </div>
      </div>
    </Slideout>
  );
}

function Bubble({ role, text, at, pending, error }) {
  const isUser = role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-v2-md px-3 py-2 text-[13px] leading-relaxed ${
        isUser
          ? 'bg-v2-action text-white'
          : error
            ? 'bg-v2-danger-soft border border-v2-danger/30 text-v2-danger'
            : 'bg-v2-surface border border-v2-border-subtle text-v2-ink'
      }`}>
        {pending && <Loader2 size={13} className="inline animate-spin mr-1" />}
        <span className="whitespace-pre-wrap">{text}</span>
        {at && (
          <div className={`text-[9.5px] mt-1 font-mono-v2 ${isUser ? 'text-white/70' : 'text-v2-ink-muted'}`}>
            {new Date(at).toLocaleTimeString()}
          </div>
        )}
      </div>
    </div>
  );
}

export default AIAssistantSlideout;
